import { db } from '@/shared/db/database';
import type { TimelogDraft } from '../../domain/entities/TimelogDraft';
import type { Timelog } from '../../domain/entities/Timelog';
import type { Employee } from '@/modules/employees/domain/entities/Employee';
import { toEmployee } from '@/modules/employees/infrastructure/mappers/employeeMapper';
import type { EmployeeDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';

export class LocalTimelogRepository {
  private _upsertPromise: Promise<void> = Promise.resolve();
  
  /**
   * Map verificationMode to deviceActionType for display in offline draft timelogs.
   *
   * Backend canonical IDs (CLOCK_WEB_MODE):
   *   11 = FACIAL_PC, 13 = PIN_PC, 2 = CARD — identity pass-through.
   *
   * Legacy pre-migration local IDs (backward compat — Dexie v5 upgrade handles these
   * for newly-written drafts, but in-flight drafts may still arrive with old IDs):
   *   1 (old local facial) → 11 (FACIAL_PC)
   *   3 (old local card)   → 2  (CARD)
   */
  private mapVerificationModeToDeviceActionType(mode: number): number | null {
    switch (mode) {
      case 11: return 11; // FACIAL_PC → identity
      case 13: return 13; // PIN_PC → identity
      case 2:  return 2;  // CARD → identity
      case 1:  return 11; // legacy local facial → FACIAL_PC (backward compat)
      case 3:  return 2;  // legacy local card → CARD (backward compat)
      default: return null;
    }
  }
  
  /**
   * Expand date range by ±1 day to handle timezone shifts and boundary cases.
   * Used by getDraftsByDateRange to ensure recent drafts appear even when
   * component state hasn't refreshed the date range.
   */
  private expandDateRange(from: string, to: string): { from: string; to: string } {
    // Parse YYYY-MM-DD strings as UTC to avoid timezone conversion issues
    const fromDate = new Date(`${from}T00:00:00Z`);
    const toDate = new Date(`${to}T23:59:59Z`);
    
    // Subtract/add milliseconds instead of using setDate to avoid local time issues
    const oneDayMs = 24 * 60 * 60 * 1000;
    const fromExpanded = new Date(fromDate.getTime() - oneDayMs);
    const toExpanded = new Date(toDate.getTime() + oneDayMs);
    
    // Extract YYYY-MM-DD using UTC methods
    const formatUTC = (d: Date) => 
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    
    return {
      from: formatUTC(fromExpanded),
      to: formatUTC(toExpanded),
    };
  }
  
  async save(draft: TimelogDraft): Promise<void> {
    // Lookup idPerson from employees table by idEntity
    let idPerson = 0;
    try {
      const allEmployees = await db.employees.toArray();
      console.log('[LocalTimelogRepository.save] Total employees in DB:', allEmployees.length);
      console.log('[LocalTimelogRepository.save] Looking for idEntity:', draft.idEntity);
      
      const employee = allEmployees.find((e) => {
        const dto = e.raw as { id_entity: number };
        return dto.id_entity === draft.idEntity;
      });
      
      if (employee) {
        idPerson = employee.idPerson;
        console.log('[LocalTimelogRepository.save] ✅ Found employee, idPerson:', idPerson);
      } else {
        console.warn('[LocalTimelogRepository.save] ❌ Employee not found for idEntity:', draft.idEntity);
        console.log('[LocalTimelogRepository.save] Available idEntities:', allEmployees.map(e => (e.raw as { id_entity: number }).id_entity));
      }
    } catch (err) {
      console.warn('[LocalTimelogRepository.save] Failed to lookup employee:', err);
    }

    console.log('[LocalTimelogRepository.save] Saving with idPerson:', idPerson);
    await db.timelogs.put({
      localId:       draft.localId,
      idPerson,
      dateTimeEvent: draft.datetime,
      syncStatus:    draft.syncStatus,
      serverId:      draft.serverId,
      raw:           draft,
    });
  }

  async markSynced(localId: string, serverId: number): Promise<void> {
    await db.timelogs.update(localId, { syncStatus: 'synced', serverId });
  }

  /**
   * Marks the local timelog as failed.
   * Only updates the timelogs table — does NOT write to syncQueue.
   * CompositeTimelogRepository is responsible for enqueuing via enqueueSyncOperation.
   */
  async markFailed(localId: string, _error: string): Promise<void> {
    await db.timelogs.update(localId, { syncStatus: 'failed' });
  }

  /**
   * Delete a draft by localId. Used only by the online path in
   * `CompositeTimelogRepository.create` when the backend responds with a
   * hard rejection that will never be resolved by retrying (e.g. duplicate
   * timelog). Never call from the offline flow — offline drafts must stay
   * so the sync worker can retry them later.
   */
  async delete(localId: string): Promise<void> {
    await db.timelogs.delete(localId);
  }

  async listPending(): Promise<TimelogDraft[]> {
    const rows = await db.timelogs
      .where('syncStatus')
      .equals('pending')
      .toArray();
    return rows.map((r) => r.raw as TimelogDraft);
  }

  /**
   * Bulk-upsert API timelogs into the local `remoteTimelogs` Dexie table.
   *
   * Inserts records that don't exist yet and updates existing ones keyed by
   * `idTimelog`. After writing records, always updates `cachedRanges` for the
   * given `accessRoleId`, expanding the stored range to the widest span ever
   * fetched (the range never shrinks on narrower subsequent queries).
   *
   * Writes are serialized via a `_upsertPromise` chain to prevent concurrent
   * IndexedDB writes from a rapid prefetch + background refresh overlap.
   *
   * @param timelogs    - Timelog entities returned from the remote API.
   * @param accessRoleId - The access role these timelogs belong to.
   * @param from        - ISO date string of the query lower bound (for range expansion).
   * @param to          - ISO date string of the query upper bound (for range expansion).
   */
  async bulkUpsert(
    timelogs: Timelog[],
    accessRoleId: number,
    from: string,
    to: string,
  ): Promise<void> {
    // Chain this operation to serialize writes
    this._upsertPromise = this._upsertPromise.then(async () => {
      // Insert/update records only if non-empty
      if (timelogs.length > 0) {
        const records = timelogs.map((t) => ({
          idTimelog: t.idTimelog,
          idAccessRole: accessRoleId,
          idPerson: t.idPerson,
          dateTimeEvent: t.datetime,
          raw: t,
        }));

        await db.remoteTimelogs.bulkPut(records);
      }

      // ALWAYS update cachedRanges, even for empty results (spec requirement)
      const existing = await db.cachedRanges.get(accessRoleId);
      const oldest = existing?.oldestCached && existing.oldestCached < from 
        ? existing.oldestCached 
        : from;
      const newest = existing?.newestCached && existing.newestCached > to 
        ? existing.newestCached 
        : to;

      await db.cachedRanges.put({
        idAccessRole: accessRoleId,
        oldestCached: oldest,
        newestCached: newest,
        lastFetchedAt: Date.now(),
      });
    });

    return this._upsertPromise;
  }

  /**
   * Fetch ONLY pending/failed drafts from db.timelogs in the given date range.
   *
   * Used by the online path in `fetchAllWithFallback` to merge drafts with remote data.
   * Does NOT query db.remoteTimelogs (only db.timelogs).
   *
   * Fix 3: correccion-de-flujo-marcajes-offline
   *
   * @param accessRoleId - Unused (kept for signature compatibility), drafts don't filter by accessRole
   * @param from         - ISO date string lower bound (inclusive).
   * @param to           - ISO date string upper bound (inclusive).
   * @returns Pending/failed drafts converted to Timelog format.
   */
  async getDraftsByDateRange(
    _accessRoleId: number,
    from: string,
    to: string,
  ): Promise<Timelog[]> {
    // Expand range by ±1 day to handle timezone shifts and component state lag
    const expanded = this.expandDateRange(from, to);
    const toWithTime = `${expanded.to}T23:59:59.999Z`;

    // Fetch local pending/failed drafts from timelogs table
    const localRows = await db.timelogs
      .where('dateTimeEvent')
      .between(expanded.from, toWithTime, true, true)
      .and((row) => row.syncStatus === 'pending' || row.syncStatus === 'failed')
      .toArray();

    if (localRows.length === 0) {
      return [];
    }

    // Load all employees once for efficient lookup (keep both DTO and Entity)
    let employeeMap: Map<number, { dto: EmployeeDTO; entity: Employee }> = new Map();
    try {
      const allEmployees = await db.employees.toArray();
      allEmployees.forEach((e) => {
        const dto = e.raw as EmployeeDTO;
        const entity = toEmployee(dto);
        employeeMap.set(entity.idEntity, { dto, entity });
      });
    } catch (err) {
      console.warn('[getDraftsByDateRange] Failed to load employees:', err);
    }

    // Convert local drafts to Timelog format with employee lookup
    const localTimelogs: Timelog[] = localRows.map((row) => {
      const draft = row.raw as TimelogDraft;

      // Lookup employee info by idEntity
      let employee: Timelog['employee'] = null;

      const empData = employeeMap.get(draft.idEntity);
      if (empData) {
        const emp = empData.entity;
        employee = {
          idPerson: emp.idPerson,
          code: emp.code,
          firstName: emp.firstName,
          lastName: emp.lastName,
          fullName: emp.fullName,
          initials: emp.initials,
          avatarColor: emp.avatarColor,
          department: emp.structureName || '',
          photoUrl: emp.photoUrl ?? null,
        };
      }

      // Resolve structureCode: prefer the value persisted at creation time
      // (resolved from the correct AccessRole/profile scope — see
      // `resolveStructureCode` in the AccessRole domain entity). Only rows
      // written before this field existed (`structureCode === undefined`)
      // fall back to the legacy employee-scoped lookup below, which is a
      // best-effort default for legacy rows only: `draft.idStructure`
      // actually comes from the access role, not the employee, so this
      // fallback can legitimately miss — threading the profile/access-role
      // scope into this infrastructure-layer read would require passing it
      // through from the presentation layer on every call, which isn't
      // available here without violating layering.
      const structureCode = draft.structureCode !== undefined
        ? draft.structureCode
        : (empData?.dto.structuresInfo.find(s => s.id_structure === draft.idStructure)?.code ?? null);

      return {
        idTimelog: -draft.idEntity, // Negative ID to differentiate from server timelogs
        idPerson: employee?.idPerson || 0,
        idEntity: draft.idEntity,
        idDevice: draft.idDevice ? Number(draft.idDevice) : null,
        datetime: draft.datetime,
        accessType: draft.accessType,
        verificationMode: draft.verificationMode,
        verificationResult: true,
        isOffline: true,
        coordinates: draft.coordinates,
        employee,
        deviceActionType: this.mapVerificationModeToDeviceActionType(draft.verificationMode),
        structureCode,
        capturePhotoUrl: draft.photo,
        source: draft.source ?? null,
        location: draft.location ?? null,
        // Slice C1: surface the Dexie row's sync status + local PK so the UI can
        // subscribe to live updates and render an accurate pending/failed pill.
        syncStatus: row.syncStatus,
        localId: row.localId,
      };
    });

    return localTimelogs;
  }

  /**
   * Fetch cached timelogs by access role and date range.
   *
   * Queries the `remoteTimelogs` table using the compound index
   * `[idAccessRole+dateTimeEvent]`, which makes range scans efficient without
   * loading unrelated access roles into memory.
   *
   * Used as the offline fallback path in `CompositeTimelogRepository.fetchAllWithFallback`.
   *
   * @param accessRoleId - The access role to query.
   * @param from         - ISO date string lower bound (inclusive).
   * @param to           - ISO date string upper bound (inclusive).
   * @returns Cached timelogs, or an empty array if none are stored for this range.
   */
  async fetchByDateRange(
    accessRoleId: number,
    from: string,
    to: string,
  ): Promise<Timelog[]> {
    console.log('[fetchByDateRange] Querying IndexedDB:', { accessRoleId, from, to });
    
    // Fix: Extend 'to' to include all times on that day
    // from: "2026-04-15" → "2026-04-15" (00:00:00 implicit)
    // to:   "2026-05-15" → "2026-05-15T23:59:59.999Z" (end of day)
    const toWithTime = `${to}T23:59:59.999Z`;
    
    // 1. Fetch server timelogs from remoteTimelogs table
    const remoteRows = await db.remoteTimelogs
      .where('[idAccessRole+dateTimeEvent]')
      .between([accessRoleId, from], [accessRoleId, toWithTime], true, true)
      .toArray();
    console.log('[fetchByDateRange] Found', remoteRows.length, 'rows in remoteTimelogs');
    
    // 2. Fetch local pending/failed drafts from timelogs table
    const localRows = await db.timelogs
      .where('dateTimeEvent')
      .between(from, toWithTime, true, true)
      .and((row) => row.syncStatus === 'pending' || row.syncStatus === 'failed')
      .toArray();
    console.log('[fetchByDateRange] Found', localRows.length, 'pending/failed drafts in timelogs');
    
    // 3. Convert remote timelogs
    const remoteTimelogs = remoteRows.map((r) => r.raw as Timelog);
    
    // 4. Load all employees once for efficient lookup (keep both DTO and Entity)
    let employeeMap: Map<number, { dto: EmployeeDTO; entity: Employee }> = new Map();
    if (localRows.length > 0) {
      try {
        const allEmployees = await db.employees.toArray();
        allEmployees.forEach((e) => {
          const dto = e.raw as EmployeeDTO;
          const entity = toEmployee(dto);
          employeeMap.set(entity.idEntity, { dto, entity });
        });
      } catch (err) {
        console.warn('[fetchByDateRange] Failed to load employees for draft lookup:', err);
      }
    }
    
    // 5. Convert local drafts to Timelog format with employee lookup
    const localTimelogs: Timelog[] = localRows.map((row) => {
      const draft = row.raw as TimelogDraft;
      
      // Lookup employee info by idEntity
      let employee: Timelog['employee'] = null;

      const empData = employeeMap.get(draft.idEntity);
      if (empData) {
        const emp = empData.entity;
        employee = {
          idPerson: emp.idPerson,
          code: emp.code,
          firstName: emp.firstName,
          lastName: emp.lastName,
          fullName: emp.fullName,
          initials: emp.initials,
          avatarColor: emp.avatarColor,
          department: emp.structureName || '',
          photoUrl: emp.photoUrl ?? null,
        };
      }

      // Resolve structureCode: prefer the value persisted at creation time
      // (resolved from the correct AccessRole/profile scope — see
      // `resolveStructureCode` in the AccessRole domain entity). Only rows
      // written before this field existed (`structureCode === undefined`)
      // fall back to the legacy employee-scoped lookup below, which is a
      // best-effort default for legacy rows only: `draft.idStructure`
      // actually comes from the access role, not the employee, so this
      // fallback can legitimately miss — threading the profile/access-role
      // scope into this infrastructure-layer read would require passing it
      // through from the presentation layer on every call, which isn't
      // available here without violating layering.
      const structureCode = draft.structureCode !== undefined
        ? draft.structureCode
        : (empData?.dto.structuresInfo.find(s => s.id_structure === draft.idStructure)?.code ?? null);

      return {
        idTimelog: -draft.idEntity, // Negative ID to differentiate from server timelogs
        idPerson: employee?.idPerson || 0,
        idEntity: draft.idEntity,
        idDevice: draft.idDevice ? Number(draft.idDevice) : null,
        datetime: draft.datetime,
        accessType: draft.accessType,
        verificationMode: draft.verificationMode,
        verificationResult: true,
        isOffline: true,
        coordinates: draft.coordinates,
        employee,
        deviceActionType: this.mapVerificationModeToDeviceActionType(draft.verificationMode),
        structureCode,
        capturePhotoUrl: draft.photo,
        source: draft.source ?? null,
        location: draft.location ?? null,
        // Slice C1: propagate Dexie row identity so the modal's live subscription
        // can key by `localId` and the pill resolves the right pending/failed state.
        syncStatus: row.syncStatus,
        localId: row.localId,
      };
    });
    
    // 5. Combine and sort by datetime descending
    const combined = [...remoteTimelogs, ...localTimelogs];
    combined.sort((a, b) => {
      const aTime = a.datetime || '';
      const bTime = b.datetime || '';
      return bTime.localeCompare(aTime);
    });
    
    console.log('[fetchByDateRange] Total combined:', combined.length, '(remote:', remoteTimelogs.length, '+ local:', localTimelogs.length, ')');
    
    return combined;
  }

  /**
   * Prune the oldest cached timelogs, keeping only the most recent `keepCount` records.
   *
   * Records are ordered descending by `dateTimeEvent`; everything beyond `keepCount`
   * is deleted in bulk. Called by `TimelogRefreshScheduler.checkAndPruneStorage()`
   * when `navigator.storage.estimate()` reports usage above 90% of quota.
   *
   * No-op if the total stored count is already within the limit.
   *
   * @param keepCount - Number of most-recent records to retain after pruning.
   */
  async pruneOldest(keepCount: number): Promise<void> {
    const all = await db.remoteTimelogs
      .orderBy('dateTimeEvent')
      .reverse()
      .toArray();

    if (all.length <= keepCount) return;

    const toDelete = all.slice(keepCount);
    const idsToDelete = toDelete.map((r) => r.idTimelog);
    await db.remoteTimelogs.bulkDelete(idsToDelete);
  }

  async getLastByPerson(idPerson: number): Promise<Timelog | null> {
    const [draftRow, remoteRow] = await Promise.all([
      db.timelogs
        .where('idPerson')
        .equals(idPerson)
        .and((row) => row.syncStatus !== 'failed')
        .reverse()
        .sortBy('dateTimeEvent')
        .then((rows) => rows[0] ?? null),
      db.remoteTimelogs
        .where('idPerson')
        .equals(idPerson)
        .reverse()
        .sortBy('dateTimeEvent')
        .then((rows) => rows[0] ?? null),
    ]);

    if (!draftRow && !remoteRow) return null;
    if (!remoteRow) return draftRowToDomain(draftRow!);
    if (!draftRow)  return remoteRowToDomain(remoteRow);

    return draftRow.dateTimeEvent >= remoteRow.dateTimeEvent
      ? draftRowToDomain(draftRow)
      : remoteRowToDomain(remoteRow);
  }

  /**
   * Returns the most recent timelog for a person with a specific accessType.
   * Used by offline consecutive-timelog validation to find the last entrada
   * or salida of the same type as the draft.
   *
   * @param idPerson — person ID
   * @param accessType — 1 (entrada) or 2 (salida)
   * @returns The most recent timelog matching the person + accessType, or null
   */
  async getLastByPersonAndAccessType(
    idPerson: number,
    accessType: 1 | 2,
  ): Promise<Timelog | null> {
    const [draftRow, remoteRow] = await Promise.all([
      db.timelogs
        .where('idPerson')
        .equals(idPerson)
        .and((row) => {
          if (row.syncStatus === 'failed') return false;
          const raw = (row.raw ?? {}) as DraftRaw;
          return (raw.accessType ?? 1) === accessType;
        })
        .reverse()
        .sortBy('dateTimeEvent')
        .then((rows) => rows[0] ?? null),
      db.remoteTimelogs
        .where('idPerson')
        .equals(idPerson)
        .reverse()
        .sortBy('dateTimeEvent')
        .then((rows) => {
          // Filter by accessType after sort
          const match = rows.find((r) => {
            const domain = r.raw as Timelog;
            return domain.accessType === accessType;
          });
          return match ?? null;
        }),
    ]);

    if (!draftRow && !remoteRow) return null;
    if (!remoteRow) return draftRowToDomain(draftRow!);
    if (!draftRow)  return remoteRowToDomain(remoteRow);

    return draftRow.dateTimeEvent >= remoteRow.dateTimeEvent
      ? draftRowToDomain(draftRow)
      : remoteRowToDomain(remoteRow);
  }
}

interface DraftRaw {
  accessType?: number;
  verificationMode?: number;
  idEntity?: number;
  idDevice?: string | number | null;
  coordinates?: { lat: number; lng: number } | null;
  source?: string | null;
  location?: string | null;
}

function draftRowToDomain(row: {
  localId: string;
  idPerson: number;
  dateTimeEvent: string;
  syncStatus: 'synced' | 'pending' | 'failed';
  serverId: number | null;
  raw: unknown;
}): Timelog {
  const raw = (row.raw ?? {}) as DraftRaw;
  const accessType: 1 | 2 = raw.accessType === 2 ? 2 : 1;
  return {
    idTimelog: row.serverId ?? 0,
    idPerson: row.idPerson,
    idEntity: raw.idEntity ?? 0,
    idDevice: typeof raw.idDevice === 'number' ? raw.idDevice : null,
    datetime: row.dateTimeEvent,
    accessType,
    verificationMode: raw.verificationMode ?? 0,
    verificationResult: true,
    isOffline: true,
    coordinates: raw.coordinates ?? null,
    employee: null,
    source: raw.source ?? null,
    location: raw.location ?? null,
    syncStatus: row.syncStatus,
    localId: row.localId,
  };
}

function remoteRowToDomain(row: {
  idTimelog: number;
  idAccessRole: number;
  idPerson: number;
  dateTimeEvent: string;
  raw: unknown;
}): Timelog {
  const domain = row.raw as Timelog;
  return { ...domain, syncStatus: 'synced', localId: null };
}
